import { useState, useCallback } from 'react';
import AIApi from '../../api/AIApi';
import useLoader from '../../util/useLoader';
import useActionReducer from 'src/util/useActionReducer';

function useAIChat(props) {
  const { agent } = props;
  const [state, dispatch] = useActionReducer(chatActions);

  const { messages, conversationId, chatContext, isOpen } = state;

  const [agentTyping, agentError, agentLoadFn] = useLoader();

  const clearChat = useCallback(() => dispatch.clearChat(), [dispatch]);
  const closeChat = useCallback(() => dispatch.close(), [dispatch]);
  const toggleOpenChat = useCallback(() => dispatch.toggleOpen(), [dispatch]);

  const sendMessage = useCallback(
    message => {
      dispatch.addSendMessage({ message });

      agentLoadFn(async () => {
        try {
          const response = await AIApi.chat({
            content: message,
            messages: messages.filter(x => x.sender !== 'System'),
            conversationId,
          });

          const aiResponse = response?.messages?.slice(-1)[0];

          if (aiResponse) {
            dispatch.addRecvMessage({
              message: aiResponse.content,
              sender: agent.name,
              conversationId: response.conversationId,
              chatContext: {
                identifierCases: response.identifierCases,
              },
            });
          } else {
            dispatch.addSystemMessage({
              message: 'No response was received.',
            });
          }
        } catch (e) {
          dispatch.addSystemMessage({
            error: true,
            message: `An error ocurred while trying to chat with ${agent.name}.`,
          });
        }
      });
    },
    [AIApi, agentLoadFn, dispatch, messages]
  );

  return {
    agent,
    agentTyping,
    agentError,
    messages,
    sendMessage,
    isOpen,
    toggleOpenChat,
    closeChat,
    chatContext,
    clearChat,
  };
}

const chatActions = {
  initialize: () => ({
    isOpen: false,
    messages: [],
    conversationId: '',
    chatContext: undefined,
  }),

  toggleOpen: (_, { isOpen }) => ({ isOpen: !isOpen }),
  close: () => ({ isOpen: false }),

  clearChat: () => ({
    messages: [],
    chatContext: undefined,
    conversationId: '',
  }),

  addSendMessage: ({ message }, state) =>
    chatActions.addMessage(
      {
        message: {
          message,
          sender: 'You',
          direction: 'outgoing',
        },
      },
      state
    ),

  addRecvMessage: ({ message, sender, conversationId, chatContext }, state) =>
    chatActions.addMessage(
      {
        message: {
          message,
          sender,
          direction: 'incoming',
        },
        conversationId,
        chatContext,
      },
      state
    ),

  addSystemMessage: ({ error, message }, state) =>
    chatActions.addMessage(
      {
        message: {
          message,
          sender: 'System',
          direction: 'incoming',
          extra: error ? { error: true } : {},
        },
      },
      state
    ),

  addMessage: ({ message: { message, sentTime, sender, direction, extra }, conversationId, chatContext }, state) => {
    const prevMessage = state?.messages ? state.messages[state.messages.length - 1] : undefined;
    const newMessage = {
      message: message ?? '',
      sentTime: sentTime ?? 'just now', // [gio]: Maybe this should have proper timestamps????
      sender: sender ?? 'unknown',
      isFirst: !prevMessage,
      isContinuation: false,
      direction: direction ?? 'incoming',
      ...(extra ?? {}),
    };

    newMessage.isContinuation = prevMessage?.sender == newMessage.sender && prevMessage?.direction == newMessage.direction;

    return {
      conversationId: conversationId ?? state?.conversationId,
      chatContext: chatContext ?? state?.chatContext,
      messages: [...(state?.messages ?? []), newMessage],
    };
  },
};

export default useAIChat;
